import {
  supabase,
  uid,
  isRecord,
  toStringValue,
  toNullableNumber,
  buildImageColumns,
  buildImageFocusColumns,
  getPublicImageUrl,
  getStoredImageAsset,
  getStoredImageFocus,
  resolveStoredImageUrl,
  safeDeleteStoredImage,
  sameStoredImageAsset,
  AVATAR_IMAGE_SELECT,
} from './shared'
import type { RecordLike } from './shared'
import type { WorldShell, WorldVersion, WorldVisibility } from '@/types/domain'
import type { PublicWorldResponse, SingleWorldResponse } from '@/types/api'
import type { WorldCreateData, WorldUpdateData } from '../../contract'

const WORLD_SHELL_SELECT = [
  'id', 'user_id', 'title', 'summary', 'visibility',
  'avatar_url', 'avatar_provider', 'avatar_bucket', 'avatar_storage_path',
  'avatar_mime_type', 'avatar_bytes', 'avatar_width', 'avatar_height', 'avatar_original_name',
  'avatar_focus_x', 'avatar_focus_y',
  'active_version_id', 'created_at', 'updated_at',
].join(', ')

const WORLD_VERSION_SELECT = [
  'id', 'world_id', 'version_number', 'title', 'summary',
  'snapshot_payload', 'draft_workspace_id', 'draft_revision',
  'published_at', 'created_at',
].join(', ')

function toVisibility(value: unknown): WorldVisibility {
  return value === 'link_public' ? 'link_public' : 'private'
}

function mapWorldShellRow(row: unknown): WorldShell {
  const safeRow = isRecord(row) ? row : {}
  return {
    id: Number(safeRow.id) || 0,
    user_id: toStringValue(safeRow.user_id),
    title: toStringValue(safeRow.title),
    summary: toStringValue(safeRow.summary),
    visibility: toVisibility(safeRow.visibility),
    avatar_url: resolveStoredImageUrl({
      row: safeRow,
      prefix: 'avatar',
      fallbackUrl: toStringValue(safeRow.avatar_url),
      getPublicUrl: getPublicImageUrl,
    }),
    avatar_focus: getStoredImageFocus(safeRow, 'avatar'),
    active_version_id: toNullableNumber(safeRow.active_version_id),
    created_at: toStringValue(safeRow.created_at),
    updated_at: toStringValue(safeRow.updated_at),
  }
}

function mapWorldVersionRow(row: unknown): WorldVersion {
  const safeRow = isRecord(row) ? row : {}
  return {
    id: Number(safeRow.id) || 0,
    world_id: Number(safeRow.world_id) || 0,
    version_number: Number(safeRow.version_number) || 0,
    title: toStringValue(safeRow.title),
    summary: toStringValue(safeRow.summary),
    snapshot_payload: isRecord(safeRow.snapshot_payload) ? safeRow.snapshot_payload : {},
    draft_workspace_id: toNullableNumber(safeRow.draft_workspace_id),
    draft_revision: toNullableNumber(safeRow.draft_revision),
    published_at: toStringValue(safeRow.published_at),
    created_at: toStringValue(safeRow.created_at),
  }
}

function buildWorldShellPayload(fields: WorldUpdateData = {}) {
  const payload: RecordLike = {}

  if (fields.title !== undefined) {
    payload.title = fields.title.trim() || 'Untitled World'
  }
  if (fields.summary !== undefined) {
    payload.summary = fields.summary
  }
  if (fields.visibility !== undefined) {
    payload.visibility = toVisibility(fields.visibility)
  }
  if (fields.avatar_url !== undefined) {
    payload.avatar_url = fields.avatar_url.trim()
  }
  if (fields.avatar_asset !== undefined) {
    Object.assign(payload, buildImageColumns('avatar', fields.avatar_asset))
  }
  if (fields.avatar_focus !== undefined) {
    Object.assign(payload, buildImageFocusColumns('avatar', fields.avatar_focus))
  }
  if (fields.avatar_asset === null) {
    Object.assign(payload, buildImageFocusColumns('avatar', null))
  }

  return payload
}

async function getActiveVersionForWorldRow(worldRow: RecordLike): Promise<WorldVersion | null> {
  if (!worldRow.active_version_id) return null

  const { data: versionRow, error: versionError } = await supabase
    .from('world_versions')
    .select(WORLD_VERSION_SELECT)
    .eq('world_id', worldRow.id)
    .eq('id', worldRow.active_version_id)
    .maybeSingle()

  if (versionError) throw versionError
  return versionRow ? mapWorldVersionRow(versionRow) : null
}

export async function getWorlds() {
  const userId = await uid()
  const { data, error } = await supabase
    .from('worlds')
    .select(WORLD_SHELL_SELECT)
    .eq('user_id', userId)
    .order('updated_at', { ascending: false })

  if (error) throw error
  return { worlds: (Array.isArray(data) ? data : []).map(mapWorldShellRow) }
}

export async function getWorld(id: number | string): Promise<SingleWorldResponse> {
  const userId = await uid()
  const { data, error } = await supabase
    .from('worlds')
    .select(WORLD_SHELL_SELECT)
    .eq('user_id', userId)
    .eq('id', id)
    .single()

  if (error) throw error
  const safeData = isRecord(data) ? data : {}
  return {
    world: mapWorldShellRow(safeData),
    active_version: await getActiveVersionForWorldRow(safeData),
  }
}

export async function getPublicWorld(id: number | string): Promise<PublicWorldResponse | null> {
  const { data: worldRow, error: worldError } = await supabase
    .from('worlds')
    .select(WORLD_SHELL_SELECT)
    .eq('id', id)
    .maybeSingle()

  if (worldError) throw worldError
  if (
    !worldRow
    || !isRecord(worldRow)
    || worldRow.visibility !== 'link_public'
    || !worldRow.active_version_id
  ) return null

  const activeVersion = await getActiveVersionForWorldRow(worldRow)
  if (!activeVersion) return null

  return {
    world: mapWorldShellRow(worldRow),
    active_version: activeVersion,
  }
}

export async function createWorld(worldData: WorldCreateData): Promise<SingleWorldResponse> {
  const userId = await uid()
  const payload = {
    user_id: userId,
    title: worldData.title.trim() || 'Untitled World',
    summary: worldData.summary ?? '',
    visibility: toVisibility(worldData.visibility),
    ...buildWorldShellPayload(worldData),
  }

  try {
    const { data, error } = await supabase
      .from('worlds')
      .insert(payload)
      .select(WORLD_SHELL_SELECT)
      .single()

    if (error) throw error
    return { world: mapWorldShellRow(data), active_version: null }
  } catch (error) {
    if (worldData.avatar_asset) {
      await safeDeleteStoredImage(worldData.avatar_asset)
    }
    throw error
  }
}

export async function updateWorld(id: number | string, worldData: WorldUpdateData = {}) {
  const userId = await uid()
  const avatarAssetProvided = Object.prototype.hasOwnProperty.call(worldData, 'avatar_asset')
  const previousAvatar = avatarAssetProvided
    ? getStoredImageAsset(
      (
        await supabase
          .from('worlds')
          .select(AVATAR_IMAGE_SELECT)
          .eq('user_id', userId)
          .eq('id', id)
          .maybeSingle()
      ).data,
      'avatar',
    )
    : null
  const payload = buildWorldShellPayload(worldData)

  try {
    if (Object.keys(payload).length) {
      const { error } = await supabase
        .from('worlds')
        .update(payload)
        .eq('user_id', userId)
        .eq('id', id)

      if (error) throw error
    }

    const result = await getWorld(id)
    if (avatarAssetProvided && previousAvatar && !sameStoredImageAsset(previousAvatar, worldData.avatar_asset)) {
      await safeDeleteStoredImage(previousAvatar)
    }
    return result
  } catch (error) {
    if (worldData.avatar_asset) {
      await safeDeleteStoredImage(worldData.avatar_asset)
    }
    throw error
  }
}

export async function deleteWorld(id: number | string) {
  const userId = await uid()
  const { error } = await supabase
    .from('worlds')
    .delete()
    .eq('user_id', userId)
    .eq('id', id)

  if (error) throw error
  return { message: 'World deleted successfully' }
}

export {
  WORLD_SHELL_SELECT,
  WORLD_VERSION_SELECT,
  buildWorldShellPayload,
  mapWorldShellRow,
  mapWorldVersionRow,
}
